/**
 * features/finance/reqWrite.ts
 * ------------------------------------------------------------------
 * Writing requisitions.
 *
 * THE NUMBER IS ALLOCATED INSIDE THE TRANSACTION, so a failed create
 * cannot burn one. A gap in a numbered series is a question an auditor
 * asks and nobody can answer.
 *
 * EVERY TRANSITION WRITES AN EVENT, and the event is written in the same
 * batch as the status it records. A status that moved without an event
 * is a requisition whose history has a hole exactly where somebody would
 * later want to look.
 *
 * THE GUARD IS lib/requisitions, NOT THIS FILE. Both this path and the
 * screen call `reqActionProblem`, so an action that never went near a
 * button is refused for the same reason and with the same sentence.
 * ------------------------------------------------------------------ */
import { collection, doc, getDocs, query, runTransaction } from 'firebase/firestore';
import { db, setDoc, updateDoc } from '@/lib/firebase';
import {
  REQ_COLLECTIONS, reqCounterId, formatReqNo, reqEstTotal, normaliseReqLine,
  reqActionProblem, REQ_ACTIONS,
  type Requisition, type ReqLine, type ReqActor, type ReqAction, type ReqEvent,
} from '@/lib/requisitions';

const now = () => new Date().toISOString();

export interface NewReqInput {
  title: string;
  department?: string;
  justification?: string;
  lines: ReqLine[];
  approverUid?: string;
  approverId?: string;
  approverName?: string;
  actor: { uid: string; name?: string };
}

/** Raise a draft. Nothing is asked of anybody until it is submitted. */
export async function createRequisition(input: NewReqInput): Promise<Requisition> {
  const lines = input.lines.map(normaliseReqLine)
    .filter((l) => l.description.trim() && l.qty > 0);
  if (lines.length === 0) throw new Error('A requisition needs at least one item.');
  if (!input.title.trim()) throw new Error('Give the requisition a title.');

  const year = now().slice(0, 4);
  const at = now();

  const created = await runTransaction(db, async (tx) => {
    const counterRef = doc(db, REQ_COLLECTIONS.counters, reqCounterId(year));
    const snap = await tx.get(counterRef);
    const seq = ((snap.exists() ? Number(snap.data().seq) : 0) || 0) + 1;
    tx.set(counterRef, { seq, updatedAt: at }, { merge: true });

    const ref = doc(collection(db, REQ_COLLECTIONS.requisitions));
    const req: Requisition = {
      id: ref.id,
      reqNo: formatReqNo(seq, year),
      title: input.title.trim(),
      ...(input.department?.trim() ? { department: input.department.trim() } : {}),
      ...(input.justification?.trim() ? { justification: input.justification.trim() } : {}),
      lines,
      estTotal: reqEstTotal(lines),
      requestedBy: input.actor.uid,
      ...(input.actor.name ? { requestedByName: input.actor.name } : {}),
      ...(input.approverUid ? { approverUid: input.approverUid } : {}),
      ...(input.approverId ? { approverId: input.approverId } : {}),
      ...(input.approverName ? { approverName: input.approverName } : {}),
      currentStatus: 'draft',
      createdAt: at,
      updatedAt: at,
    };
    tx.set(ref, req);

    const evRef = doc(collection(db, REQ_COLLECTIONS.requisitions, ref.id, REQ_COLLECTIONS.eventsSub));
    tx.set(evRef, {
      id: evRef.id, type: 'created', status: 'draft',
      by: input.actor.uid, byName: input.actor.name ?? '', at,
    } satisfies ReqEvent);

    return req;
  });

  return created;
}

/** Edit a draft. Only a draft, and only its own author — enforced by rules too. */
export async function updateDraftRequisition(
  req: Requisition,
  patch: Partial<Pick<Requisition, 'title' | 'department' | 'justification'
    | 'approverUid' | 'approverId' | 'approverName'>> & { lines?: ReqLine[] },
): Promise<void> {
  if (req.currentStatus !== 'draft') {
    throw new Error('Only a draft can be edited. Submit it, or ask for it to be rejected.');
  }
  const lines = patch.lines?.map(normaliseReqLine).filter((l) => l.description.trim() && l.qty > 0);
  await updateDoc(doc(db, REQ_COLLECTIONS.requisitions, req.id), {
    ...patch,
    ...(lines ? { lines, estTotal: reqEstTotal(lines) } : {}),
    updatedAt: now(),
  });
}

/**
 * Move a requisition along.
 *
 * The stamps written per action are the denormalised view of the event
 * just recorded — lists and the waiting report read them so they do not
 * have to open every subcollection.
 */
export async function transitionRequisition(
  req: Requisition,
  action: Exclude<ReqAction, 'reject'>,
  actor: ReqActor & { name?: string },
  comment?: string,
): Promise<void> {
  const problem = reqActionProblem(req, action, actor);
  if (problem) throw new Error(problem);

  const def = REQ_ACTIONS.find((a) => a.key === action)!;
  const at = now();

  const stamps: Record<string, unknown> = { currentStatus: def.to, updatedAt: at };
  if (action === 'submit') stamps.submittedAt = at;
  if (action === 'approverApprove') {
    stamps.approverApprovedAt = at;
    stamps.approverApprovedBy = actor.uid;
  }
  if (action === 'operationsApprove') {
    stamps.operationsApprovedAt = at;
    stamps.operationsApprovedBy = actor.uid;
    stamps.operationsApprovedByName = actor.name ?? '';
  }
  if (action === 'order') stamps.orderedAt = at;
  if (action === 'close') stamps.closedAt = at;

  await updateDoc(doc(db, REQ_COLLECTIONS.requisitions, req.id), stamps);
  await appendEvent(req.id, {
    type: def.eventType, status: def.to,
    by: actor.uid, byName: actor.name ?? '', at, comment,
  });
}

/** Refusing costs a reason. "Rejected" with no explanation gets re-raised. */
export async function rejectRequisition(
  req: Requisition,
  reason: string,
  actor: ReqActor & { name?: string },
): Promise<void> {
  const problem = reqActionProblem(req, 'reject', actor);
  if (problem) throw new Error(problem);
  if (!reason.trim()) throw new Error('Say why it is being refused — a bare rejection gets re-raised.');

  const at = now();
  await updateDoc(doc(db, REQ_COLLECTIONS.requisitions, req.id), {
    currentStatus: 'rejected',
    rejectedAt: at,
    rejectedBy: actor.uid,
    rejectedReason: reason.trim(),
    updatedAt: at,
  });
  await appendEvent(req.id, {
    type: 'rejected', status: 'rejected',
    by: actor.uid, byName: actor.name ?? '', at, comment: reason.trim(),
  });
}

/** Record which order was raised from this requisition, closing the loop. */
export async function linkOrderToRequisition(
  reqId: string, poId: string, poNo: string,
): Promise<void> {
  await updateDoc(doc(db, REQ_COLLECTIONS.requisitions, reqId), {
    poId, poNo, updatedAt: now(),
  });
}

export async function commentOnRequisition(
  reqId: string, comment: string, actor: { uid: string; name?: string },
): Promise<void> {
  if (!comment.trim()) return;
  await appendEvent(reqId, {
    type: 'comment', by: actor.uid, byName: actor.name ?? '',
    at: now(), comment: comment.trim(),
  });
}

async function appendEvent(reqId: string, ev: Omit<ReqEvent, 'id'>): Promise<void> {
  const ref = doc(collection(db, REQ_COLLECTIONS.requisitions, reqId, REQ_COLLECTIONS.eventsSub));
  await setDoc(ref, { id: ref.id, ...ev });
}

export async function fetchRequisitionEvents(reqId: string): Promise<ReqEvent[]> {
  const snap = await getDocs(query(
    collection(db, REQ_COLLECTIONS.requisitions, reqId, REQ_COLLECTIONS.eventsSub),
  ));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as ReqEvent)
    .sort((a, b) => (a.at ?? '').localeCompare(b.at ?? ''));
}
